import React, { useState } from "react";
import { Clock, Users, Loader2 } from "lucide-react";
import { TabsContent } from "@/components/ui/tabs";
import { format, parseISO } from "date-fns";
import getOrderTipsDetails from "@/lib/orderTipsUtils";
import { 
  OrderItemPriceDisplay, 
  OrderItemComboBreakdown, 
  OrderItemCourseBreakdown, 
  OrderItemMixAndMatchBreakdown, 
  OrderItemCustomizationBreakdown, 
  OrderItemSpecialMenuBreakdown 
} from "@/components/TakeOder/OrderItemComboBreakdown";
import OrderedItemDetailsModal from "@/components/TakeOder/OrderedItemDetailsModal";

const formatStatusText = (text) => {
  if (!text) return "";
  return String(text)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const OrderSkeleton = () => (
  <div className="flex flex-col xl:flex-row min-h-[600px] bg-[#1E1E1E] text-white rounded-lg overflow-hidden border border-gray-800 animate-pulse">
    {/* Left Content Area Skeleton */}
    <div className="flex-1 p-4 lg:p-6 flex flex-col space-y-6">
      <div className="flex justify-between items-start gap-4">
        <div className="h-10 w-24 bg-neutral-800 rounded"></div>
        <div className="h-10 w-40 bg-neutral-800 rounded"></div>
        <div className="h-10 w-32 bg-neutral-800 rounded"></div>
        <div className="h-10 w-28 bg-neutral-800 rounded"></div>
      </div>
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-12 w-full bg-neutral-800 rounded"></div>
        ))}
      </div>
      <div className="h-32 w-full bg-neutral-800 rounded"></div>
    </div>
    {/* Sidebar Skeleton */}
    <div className="w-full xl:w-[340px] bg-[#222222] p-6 lg:p-8 flex flex-col space-y-4 border-t xl:border-t-0 xl:border-l border-gray-800">
      <div className="h-6 w-32 bg-neutral-800 rounded"></div>
      <div className="space-y-3 pt-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="flex justify-between">
            <div className="h-4 w-24 bg-neutral-800 rounded"></div>
            <div className="h-4 w-12 bg-neutral-800 rounded"></div>
          </div>
        ))}
      </div>
      <div className="border-t border-dashed border-gray-600 my-4 pt-4 space-y-4">
        <div className="flex justify-between">
          <div className="h-6 w-28 bg-neutral-800 rounded"></div>
          <div className="h-6 w-16 bg-neutral-800 rounded"></div>
        </div>
      </div>
    </div>
  </div>
);

const BillingSidebar = ({ order }) => {
  const subTotal = Number(order?.subtotal ?? order?.order_total ?? order?.sub_total ?? order?.total_price ?? 0);
  const discount = Number(order?.discount_amount ?? order?.discount_total ?? 0) + Number(order?.loyalty_discount_amount ?? order?.loyalty_discount_total ?? 0);
  const serviceCharge = Number(order?.service_charge_amount ?? order?.service_charge ?? 0);
  const vat = Number(order?.total_vat ?? order?.vat?.total_vat ?? order?.vat_amount ?? 0);
  const vatType = order?.vat_type || order?.vat?.type || "";
  const tipsInfo = getOrderTipsDetails(order);
  const tips = tipsInfo.totalTips;
  const total = Number(order?.total_price ?? (subTotal - discount + serviceCharge + (vatType === "exclusive" ? vat : 0) + tips));

  const billingItems = [
    { label: "Order Total", value: subTotal.toFixed(2) },
    { label: "Discount Sum", value: discount.toFixed(2) },
    { label: "Service Charge", value: serviceCharge.toFixed(2) },
    { label: "Sub Total", value: (subTotal - discount + serviceCharge).toFixed(2) },
    { label: vatType ? `VAT Amount (${vatType})` : "VAT Amount", value: vat.toFixed(2) },
  ];

  return (
    <div className="w-full xl:w-[340px] bg-[#222222] p-6 lg:p-8 flex flex-col border-t xl:border-t-0 xl:border-l border-gray-800">
      <h2 className="text-white text-xl font-semibold mb-6">Billing Details</h2>

      <div className="space-y-3 mb-6">
        {billingItems.map((item, idx) => (
          <div key={idx} className="flex justify-between text-sm">
            <span className="text-gray-400">{item.label}</span>
            <span className="text-gray-300 font-medium">£ {item.value}</span>
          </div>
        ))}
        <div className="flex justify-between text-sm">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-gray-400">TIPS</span>
            {tipsInfo.tipsMode === "course_wise" && (
              <span className="bg-yellow-500/20 text-yellow-400 text-[10px] px-1.5 py-0.5 rounded font-medium">Course-wise</span>
            )}
            {tipsInfo.tipsMode === "split" && (
              <span className="bg-blue-500/20 text-blue-400 text-[10px] px-1.5 py-0.5 rounded font-medium">Split</span>
            )}
          </div>
          <span className={`font-medium ${tips > 0 ? "text-yellow-400 font-bold" : "text-gray-300"}`}>
            £ {tips.toFixed(2)}
          </span>
        </div>
        {tipsInfo.breakdown && tipsInfo.breakdown.length > 0 && (
          <div className="-mt-1 mb-1 pl-2 pr-1.5 py-1 bg-black/30 rounded border border-white/5 flex flex-col gap-0.5">
            {tipsInfo.breakdown.map((person, idx) => (
              <div key={idx} className="flex justify-between items-center text-[11px] text-gray-400">
                <span className="truncate">
                  • {person.payerName} {person.tipType === "percentage" ? `(${person.tipInputAmount}%)` : `(Fixed)`}:
                </span>
                <span className="font-medium text-gray-300 shrink-0">
                  £ {person.tipAmount.toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-dashed border-gray-600 my-4 pt-4 space-y-4">
        <div className="flex justify-between text-base font-bold">
          <span className="text-gray-200">TOTAL TO PAY</span>
          <span className="text-gray-200">£ {total.toFixed(2)}</span>
        </div>
        {order?.payment_method && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-400 italic">Method</span>
            <span className="text-gray-400 capitalize">{order.payment_method}</span>
          </div>
        )}
      </div>

      <div className="mt-8 flex justify-between items-end">
        <div className="flex flex-col">
          <span className="text-yellow-500 font-bold text-[10px] uppercase tracking-widest mb-1">
            Payment Status
          </span>
          <span className={`font-bold text-sm uppercase ${order?.payment_status === "paid" ? "text-green-500" : "text-yellow-500"}`}>
            {order?.payment_status ? String(order.payment_status).replace(/_/g, " ").toUpperCase() : "UNPAID"}
          </span>
        </div>
        <span className="text-gray-200 font-black text-xl tracking-tighter opacity-50">
          #{order?.day_order_number || order?.id || ""}
        </span>
      </div>
    </div>
  );
};

export default function OrderDetailsTabs({ singleOrder = null, singleOrderLoading = false }) {
  const [viewItemDetails, setViewItemDetails] = useState(null);

  if (singleOrderLoading) {
    return (
      <TabsContent value="order-details">
        <OrderSkeleton />
      </TabsContent>
    );
  }

  if (!singleOrder) {
    return (
      <TabsContent value="order-details">
        <div className="flex flex-col items-center justify-center min-h-[300px] bg-[#1E1E1E] text-gray-400 border border-gray-800 rounded-lg">
          <p className="text-lg">No order details found for this booking.</p>
        </div>
      </TabsContent>
    );
  }

  const orderTimeStr = singleOrder.created_at
    ? format(parseISO(singleOrder.created_at), "hh:mm a")
    : "N/A";

  const staffName = singleOrder?.staff?.full_name || singleOrder?.waiter || singleOrder?.operator || "-";

  return (
    <TabsContent value="order-details">
      <div className="flex flex-col xl:flex-row min-h-[600px] bg-[#1E1E1E] text-white font-sans selection:bg-yellow-500/30 rounded-lg overflow-hidden border border-gray-800">
        {/* Left Content Area */}
        <div className="flex-1 p-4 lg:p-6 flex flex-col">
          {/* Header metadata summary */}
          <div className="flex flex-wrap lg:flex-nowrap justify-between items-start mb-6 px-1 gap-4 lg:gap-0">
            <div className="flex flex-col text-[13px] leading-tight min-w-[100px]">
              <span className="text-gray-400 text-xs">Ordered At</span>
              <span className="text-gray-300 font-semibold">{orderTimeStr}</span>
            </div>

            <div className="flex flex-col lg:ml-8 min-w-[150px]">
              <h2 className="text-gray-200 text-sm font-medium">Waiter/Staff</h2>
              <div className="flex items-center gap-1 text-gray-400 text-xs mt-1">
                <Users size={14} />
                <span>{staffName}</span>
              </div>
            </div>

            <div className="flex flex-col items-center flex-1 min-w-[150px]">
              <span className="text-gray-400 text-xs mb-1">Type</span>
              <div className="flex items-center gap-2 text-yellow-500 font-bold text-lg">
                <span>{singleOrder.order_type ? formatStatusText(singleOrder.order_type) : "Table"}</span>
              </div>
            </div>

            <div className="flex flex-col items-end min-w-[120px]">
              <span className="text-gray-300 text-[11px] mb-2 uppercase tracking-wider">
                ID: {singleOrder.unique_id || singleOrder.id}
              </span>
              <button className="bg-green-500 text-white text-[11px] font-bold py-1.5 px-6 rounded-full transition-colors whitespace-nowrap">
                {formatStatusText(singleOrder.status || singleOrder.stage || "Completed")}
              </button>
            </div>
          </div>

          {/* Items List Section */}
          <div className="flex-1">
            <div className="rounded-lg overflow-hidden flex flex-col gap-2">
              {singleOrder.items && singleOrder.items.length > 0 ? (
                singleOrder.items.map((item, index) => {
                  const qty = Number(item.quantity || 1);
                  const calculatedRowPrice = Number(item.calculated_price ?? item.price ?? item.base_price ?? 0);
                  const totalItemPrice = calculatedRowPrice * qty;

                  return (
                    <div 
                      key={item.order_item_id || item.id || index} 
                      className="bg-[#262626] border border-[#333] hover:border-[#444] rounded-lg p-3.5 flex flex-col gap-2 transition-colors"
                    >
                      {/* Item Main Row */}
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                        <div className="flex items-center gap-2 flex-wrap min-w-0 flex-1">
                          <span className="text-gray-100 font-semibold text-sm md:text-base">
                            {item.name || "Item"}
                          </span>

                          {item.variant?.name && (
                            <span className="text-xs text-yellow-500 bg-[#1f1f1f] px-2 py-0.5 rounded border border-[#333]">
                              ({item.variant.name})
                            </span>
                          )}

                          {item.size && (
                            <span className="text-xs text-yellow-500 bg-[#1f1f1f] px-2 py-0.5 rounded border border-[#333]">
                              ({item.size})
                            </span>
                          )}

                          {item.buffet_price_for && (
                            <span className="text-xs text-yellow-500 bg-[#1f1f1f] px-2 py-0.5 rounded border border-[#333]">
                              ({item.buffet_price_for === "adult" ? "Adult Buffet" : "Kids Buffet"})
                            </span>
                          )}

                          <span className="text-xs font-bold text-yellow-500 bg-[#1f1f1f] px-2 py-0.5 rounded border border-[#333]">
                            x{qty}
                          </span>

                          {item.is_offer_discount && (
                            <span className="text-[10px] bg-emerald-600/20 text-emerald-400 border border-emerald-500/40 px-1.5 py-0.5 rounded font-medium">
                              🏷️ Discount Offer
                            </span>
                          )}

                          {item.is_complementary && (
                            <span className="text-[10px] bg-purple-500/20 text-purple-300 border border-purple-500/40 px-1.5 py-0.5 rounded font-medium">
                              ✨ Complementary
                            </span>
                          )}

                          {(item.is_loyalty_free || item.is_offer_free || item.is_free) && !item.is_complementary && (
                            <span className="text-[10px] bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 px-1.5 py-0.5 rounded font-medium">
                              Free
                            </span>
                          )}

                          {item.is_replaced && (
                            <span className="text-[10px] bg-blue-500/20 text-blue-300 border border-blue-500/40 px-1.5 py-0.5 rounded font-medium">
                              Replaced
                            </span>
                          )}
                        </div>

                        {/* Price Breakdown and View Details Action */}
                        <div className="flex items-center justify-between sm:justify-end w-full sm:w-auto gap-4 shrink-0">
                          <OrderItemPriceDisplay item={item} orderType={singleOrder?.order_type} />

                          <span className="text-yellow-500 text-sm font-bold min-w-[75px] text-right">
                            £ {totalItemPrice.toFixed(2)}
                          </span>

                          <button
                            type="button"
                            onClick={() => setViewItemDetails(item)}
                            className="text-xs text-yellow-500 hover:text-yellow-400 underline underline-offset-2 transition-colors cursor-pointer whitespace-nowrap ml-1 font-medium"
                          >
                            View Details
                          </button>
                        </div>
                      </div>

                      {/* Structured Item-Specific Breakdowns */}
                      <OrderItemComboBreakdown comboItems={item.combo_items} />
                      <OrderItemCourseBreakdown courseItems={item.course_items} item={item} />
                      <OrderItemSpecialMenuBreakdown specialMenu={item.special_menu} item={item} />
                      <OrderItemMixAndMatchBreakdown items={item.mix_and_match_items || item.selected_items || item.categories} />
                      <OrderItemCustomizationBreakdown item={item} />

                      {/* Replaced item new items stack */}
                      {item.is_replaced && Array.isArray(item.new_items) && item.new_items.length > 0 && (
                        <div className="mt-1 pt-2 border-t border-[#383838] flex flex-col gap-1.5">
                          <span className="text-[11px] font-semibold text-green-400">New Replacement Items:</span>
                          {item.new_items.map((newItem, nIdx) => (
                            <div key={nIdx} className="bg-[#1f1f1f] rounded p-2.5 flex flex-col gap-1.5 border border-[#333]">
                              <div className="flex items-center justify-between gap-2 flex-wrap">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-xs font-semibold text-white">{newItem.name}</span>
                                  {newItem?.variant?.name && (
                                    <span className="text-[10px] text-yellow-500">({newItem.variant.name})</span>
                                  )}
                                  <span className="text-[10px] text-yellow-500 font-bold">x{newItem.quantity || 1}</span>
                                  <span className="px-1.5 py-0.2 bg-green-500/20 text-green-400 border border-green-500/40 text-[9px] rounded font-semibold">New</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <OrderItemPriceDisplay item={newItem} orderType={singleOrder?.order_type} />
                                  <span className="text-yellow-500 text-xs font-bold">
                                    £ {(Number(newItem.calculated_price ?? newItem.price ?? 0) * Number(newItem.quantity || 1)).toFixed(2)}
                                  </span>
                                </div>
                              </div>
                              <OrderItemComboBreakdown comboItems={newItem.combo_items} />
                              <OrderItemCourseBreakdown courseItems={newItem.course_items} item={newItem} />
                              <OrderItemSpecialMenuBreakdown specialMenu={newItem.special_menu} item={newItem} />
                              <OrderItemMixAndMatchBreakdown items={newItem.mix_and_match_items || newItem.selected_items || newItem.categories} />
                              <OrderItemCustomizationBreakdown item={newItem} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <p className="text-gray-500 text-sm italic p-6 text-center bg-[#262626] rounded-lg">
                  No items in this order
                </p>
              )}
            </div>
          </div>

          {/* Bottom Processing Note */}
          <div className="min-h-[50px] bg-[#262626]/70 mt-6 rounded-lg border border-gray-800/80 flex items-center px-4 py-3">
            <span className="text-gray-400 text-xs italic">
              Order processing notes: {singleOrder.note || "None"}
            </span>
          </div>
        </div>

        {/* Right Sidebar */}
        <BillingSidebar order={singleOrder} />
      </div>

      {/* Full Deep-Dive Item Details Modal */}
      {viewItemDetails && (
        <OrderedItemDetailsModal
          isOpen={Boolean(viewItemDetails)}
          onClose={() => setViewItemDetails(null)}
          item={viewItemDetails}
          orderType={singleOrder?.order_type}
        />
      )}
    </TabsContent>
  );
}
